import { Bell } from "lucide-react";
import { useState } from "react";
import { useParams } from "react-router-dom";

import { useInfiniteScrollNotifications, useNotificationWebSocketListener } from "./hooks";
import NoNotifications from "./NoNotifications";
import NotificationsList from "./NotificationsList";

import LoadingSpinner from "@/components/LoadingSpinner";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { useAuth } from "@/context/auth-context";
import {
  useClearNotifications,
  useDismissNotification,
  useNotifications,
} from "@/queries/notifications";

const Notifications = () => {
  const [open, setOpen] = useState(false);
  const [dismissedIds, setDismissedIds] = useState<number[]>([]);
  const { workspaceId } = useParams();
  const auth = useAuth();
  const userToken = auth?.userToken ?? "";

  // Listen for real-time notification updates via WebSocket
  useNotificationWebSocketListener();

  // Reset dismissed IDs when popover closes to show fresh state on reopen
  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setDismissedIds([]);
    }
  };

  const notificationsQuery = useNotifications({ per_page: 20 });
  const dismissMutation = useDismissNotification(userToken);
  const clearMutation = useClearNotifications(userToken);

  const { visibleNotifications, observerTarget, isLoading, isFetchingNextPage, hasNextPage } =
    useInfiniteScrollNotifications({
      notificationsQuery,
      dismissedIds,
    });

  const hasNotifications = !isLoading && visibleNotifications.length > 0;

  const handleDismiss = (id: number) => {
    // Optimistically update UI
    setDismissedIds(prev => [...prev, id]);

    // Call API
    dismissMutation.mutate(
      {
        workspaceId: Number(workspaceId),
        notificationId: id,
      },
      {
        onError: () => {
          // Revert optimistic update on error
          setDismissedIds(prev => prev.filter(dismissedId => dismissedId !== id));
        },
      },
    );
  };

  const handleClearAll = () => {
    // Get all visible notification IDs
    const allIds = visibleNotifications.map(n => n.id);

    // Optimistically update UI
    setDismissedIds(prev => [...prev, ...allIds]);

    // Call API
    clearMutation.mutate(
      {
        workspaceId: Number(workspaceId),
      },
      {
        onError: () => {
          // Revert optimistic update on error
          setDismissedIds(prev => prev.filter(id => !allIds.includes(id)));
        },
      },
    );
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="p-8 text-center">
          <LoadingSpinner />
        </div>
      );
    }

    if (hasNotifications) {
      return (
        <NotificationsList
          notifications={visibleNotifications}
          onDismiss={handleDismiss}
          observerTarget={observerTarget}
          isFetchingNextPage={isFetchingNextPage}
          hasNextPage={hasNextPage}
        />
      );
    }

    return <NoNotifications />;
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <BasicTooltip content="Notifications">
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="relative h-8 w-8 rounded-md text-gray-600 transition-colors hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
          >
            <Bell className="h-[18px] w-[18px]" />
            {hasNotifications && (
              <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-blue-500 ring-2 ring-white dark:ring-gray-900" />
            )}
          </Button>
        </PopoverTrigger>
      </BasicTooltip>
      <PopoverContent className="w-[400px] p-0 shadow-lg" align="center">
        <div className="flex items-center justify-between border-b border-gray-100/80 px-5 py-3.5 dark:border-gray-800/80">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Notifications</h4>
          {hasNotifications && (
            <button
              onClick={handleClearAll}
              disabled={clearMutation.isPending}
              className="text-xs font-medium text-gray-500 transition-colors hover:text-gray-700 disabled:opacity-50 dark:text-gray-400 dark:hover:text-gray-200"
            >
              Clear all
            </button>
          )}
        </div>
        <div
          className={
            hasNotifications
              ? "scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent hover:scrollbar-thumb-gray-400 dark:scrollbar-thumb-gray-600 dark:hover:scrollbar-thumb-gray-500 max-h-[500px] overflow-y-auto scroll-smooth"
              : "p-8 text-center"
          }
        >
          {renderContent()}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default Notifications;
